// What each Roll20 token bar means — the gate every token write and every
// bar read goes through. Party tokens and NPC tokens have separate layouts
// (compendium monsters land with HP on Bar 1; the party keeps HP on Bar 3),
// both chosen by the DM under Roll20 options. A bar set to "none" is never
// written and never read. Bar 4 exists only in games that have it.
import type { Character, Layout } from "./types";
import { AURAS } from "./auras";

export type BarNo = 1 | 2 | 3 | 4;
export type BarRole = "none" | "hp" | "temp" | "ac" | "concentration" | "auraRoll";
export interface BarLayout { 1: BarRole; 2: BarRole; 3: BarRole; 4: BarRole }
export interface BarValue { value: string; max: string; link?: string }

export const BAR_NUMBERS: BarNo[] = [1, 2, 3, 4];
export const BAR_COLOURS: Record<BarNo, string> = { 1: "green", 2: "blue", 3: "red", 4: "yellow" };
export const BAR_ROLES: { value: BarRole; label: string; hint: string }[] = [
  { value: "none", label: "— leave alone", hint: "The DM Screen never writes or reads this bar." },
  { value: "hp", label: "HP / max HP", hint: "Current hit points over the maximum; edits on the token flow back to the sheet." },
  { value: "temp", label: "temp HP", hint: "Temporary hit points (blank when none); an aura's temp HP lands here." },
  { value: "ac", label: "AC", hint: "Armor class, no max." },
  { value: "concentration", label: "concentration (rounds left)", hint: "Rounds left on a concentration aura over its full duration; blank when not concentrating." },
  { value: "auraRoll", label: "aura roll", hint: "The number the character's aura rolled for this turn (Twilight temp HP, Spirit Guardians damage)." },
];

export const DEFAULT_PC_BARS: BarLayout = { 1: "temp", 2: "ac", 3: "hp", 4: "none" };
export const DEFAULT_NPC_BARS: BarLayout = { 1: "hp", 2: "ac", 3: "temp", 4: "none" };

const isRole = (v: unknown): v is BarRole => BAR_ROLES.some((r) => r.value === v);
function readLayout(raw: unknown, fallback: BarLayout): BarLayout {
  const r = (raw || {}) as Partial<Record<string, unknown>>;
  const out = { ...fallback };
  for (const n of BAR_NUMBERS) if (isRole(r[String(n)])) out[n] = r[String(n)] as BarRole;
  return out;
}
export const pcBarLayout = (layout: Layout): BarLayout => readLayout(layout.tokenBarsPc, DEFAULT_PC_BARS);
export const npcBarLayout = (layout: Layout): BarLayout => readLayout(layout.tokenBarsNpc, DEFAULT_NPC_BARS);

/** The bar carrying a role, or null when the layout has none for it. */
export function barFor(bars: BarLayout, role: BarRole): BarNo | null {
  for (const n of BAR_NUMBERS) if (bars[n] === role) return n;
  return null;
}
/** The role a bar key like "bar3_value" carries. */
export function roleOfKey(bars: BarLayout, key: string): BarRole | null {
  const m = key.match(/^bar([1-4])_/);
  return m ? bars[Number(m[1]) as BarNo] : null;
}

/** What a party character puts in each bar under this layout. */
export function barValuesFor(c: Character, bars: BarLayout): Partial<Record<`bar${BarNo}`, BarValue>> {
  const out: Partial<Record<`bar${BarNo}`, BarValue>> = {};
  for (const n of BAR_NUMBERS) {
    const v = valueForRole(c, bars[n]);
    if (v) out[`bar${n}`] = v;
  }
  return out;
}

/* WHAT A BAR SHOULD SAY FOR THIS CHARACTER.

   Every branch goes through `num`, which returns "" for anything that is not a
   finite number. Audited after a token's Bar 3 was overwritten with the text
   "NaN" on 2026-09-29: `hp` here read String(Number(c.hp || 0)), and a character
   whose hp was ever a non-numeric string would have written "NaN" onto the token
   in the same way. `ac` and `temp` were safe only by accident — NaN is falsy, so
   `|| ""` swallowed it, and `NaN > 0` is false — which is not a property to rely
   on in four places written at different times.

   An empty string is the right refusal: Roll20 shows an empty bar, which is
   visibly "unknown" rather than a number the DM might act on. */
const num = (v: unknown, blankAtZero = false): string => {
  /* THE DEFAULT WAS THE BUG.

     This read `Number(v ?? 0)`, and `??` only catches null and undefined -- so a
     BLANK fell through to `Number("")`, which is 0, and wrote "0" onto the bar.
     null was worse: `null ?? 0` IS 0, so the default turned "unknown" into a
     hit-point total directly.

     Measured 2026-10-01: a character with hp "", "  " or null put a 0 on its
     Roll20 HP bar, which the DM reads as a creature dropping. The hardening
     after the NaN incident caught NaN and text and stopped there, because the
     fault it was written for arrived as NaN -- and the comment above has said
     "an empty string is the right refusal" the whole time. */
  if (v === null || v === undefined || String(v).trim() === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (blankAtZero && n === 0) return "";
  return String(Math.round(n));
};
export function valueForRole(c: Character, role: BarRole): BarValue | null {
  switch (role) {
    case "hp": return { value: num(c.hp), max: num(c.maxHp, true) };
    case "temp": { const t = Number(c.tempHp || 0); return Number.isFinite(t) && t > 0 ? { value: num(t), max: num(t) } : { value: "", max: "" }; }
    case "ac": return { value: num(c.ac, true), max: "" };
    case "concentration": {
      const a = (c.auras || []).find((x) => x.concentration && x.remaining !== null && x.remaining !== undefined);
      return a ? { value: num(a.remaining), max: num(a.total || AURAS.find((d) => d.key === a.key)?.rounds, true) } : { value: "", max: "" };
    }
    case "auraRoll": {
      const a = (c.auras || []).find((x) => x.roll !== undefined && x.roll !== null);
      return a ? { value: num(a.roll), max: "" } : { value: "", max: "" };
    }
    default: return null;
  }
}

/** One line for the options panel / help: "temp · AC · HP / max · —". */
export const describeLayout = (bars: BarLayout) => BAR_NUMBERS.map((n) => `${n} ${BAR_ROLES.find((r) => r.value === bars[n])?.label || bars[n]}`).join(" · ");

/* TEMPORARY HIT POINTS DO NOT STACK.
 *
 * You take the better of what you have and what you are offered — never the sum.
 * Pulled out of the aura path so the rule is stated once and tested: it lived
 * inline as `have < roll` against a token read earlier in the turn, so a stale low
 * reading could LOWER temporary hit points something else had granted since.
 *
 * `now` must be what the token holds at the moment of writing, not when the aura's
 * reach was computed.
 */
export type TempHpOutcome = { kind: "raise"; to: number } | { kind: "keep"; at: number } | { kind: "refuse"; why: string };
export function temporaryHpOutcome(now: unknown, offered: unknown): TempHpOutcome {
  const at = Number(now ?? 0);
  const to = Number(offered ?? 0);
  if (!Number.isFinite(to)) return { kind: "refuse", why: "the rolled amount was not a number" };
  /* An unreadable current value is not a reason to refuse: a creature with a blank
     temporary-HP bar has none, and the offer should land. */
  const held = Number.isFinite(at) ? at : 0;
  if (to <= 0) return { kind: "refuse", why: "nothing was rolled" };
  return held >= to ? { kind: "keep", at: held } : { kind: "raise", to };
}
